'use client';

import cx from 'classnames';

import { DestinationCard } from '@/src/components/destination-card/DestinationCard';
import { IResolvedCrossSale } from '@/src/utils/collections/cross-sale.mapper';
import { CrossSaleAction } from '@/src/components/cross-sale/CrossSaleAction/CrossSaleAction';
import { ILocaleType } from '@/src/types';
import { formatVolume } from '@/src/utils/format/volume';
import { applyDiscount } from '@/src/utils/format/amount';
import {
  EDestinationSwitcher,
  useCrossSale,
} from '@/src/components/cross-sale/useCrossSale';
import { CrossSaleDestinationSwitcher } from '@/src/components/cross-sale/CrossSaleDestinationSwitcher/CrossSaleDestinationSwitcher';
import { CrossSaleDurationSwitcher } from '@/src/components/cross-sale/CrossSaleDurationSwitcher/CrossSaleDurationSwitcher';
import { generateDataLayerClickItemList } from '@/src/utils/seo/analytics';

type ICrossSaleProps = {
  resolvedCrossSale: IResolvedCrossSale;
  locale: ILocaleType;
};

export const CrossSale = ({ resolvedCrossSale, locale }: ICrossSaleProps) => {
  const {
    action,
    destinations,
    destinationSwitcher,
    durationSwitcher,
    popularDestinations,
    title,
  } = resolvedCrossSale;
  const {
    destinationTypeSwitcher,
    setDestinationTypeSwitcher,
    selectedDuration,
    setSelectedDuration,
    setIsExpanded,
    isExpanded,
    zones,
  } = useCrossSale({
    destinations,
    popularDestinations,
  });

  return (
    <div className={'mx-auto max-w-1200 px-4 py-18'}>
      <div className={'flex flex-col text-center md:flex-row'}>
        {title && (
          <span className={'mb-10 text-4xl font-semibold lg:text-6xl'}>
            {title}
          </span>
        )}
        {durationSwitcher && (
          <CrossSaleDurationSwitcher
            setDurationSwitcher={setSelectedDuration}
            durationSwitcher={selectedDuration}
            durations={resolvedCrossSale.durations}
            locale={locale}
            durationRegexp={resolvedCrossSale.durationRegexp}
          />
        )}
      </div>
      {destinationSwitcher && (
        <CrossSaleDestinationSwitcher
          setDestinationTypeSwitcher={setDestinationTypeSwitcher}
          destinationTypeSwitcher={destinationTypeSwitcher}
          subTitle={destinationSwitcher.subTitle}
          allDestinationsText={destinationSwitcher.buttonAllDestinations}
          mostPopularText={destinationSwitcher.buttonMostPopular}
          zonesText={destinationSwitcher.buttonZones}
        />
      )}
      <div
        className={cx(
          'grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-6 my-4 overflow-hidden',
          isExpanded ? ` max-h-none ` : 'max-h-[470px] lg:max-h-[420px]'
        )}
      >
        {destinationTypeSwitcher === EDestinationSwitcher.MOST_POPULAR &&
          Object.values(popularDestinations).map(value => {
            if (value.offers.length > 0) {
              const offer = value.offers.find(
                o => o.duration === selectedDuration
              );

              if (offer)
                return (
                  <DestinationCard
                    key={offer.zone}
                    allowanceInfo={resolvedCrossSale.allowanceInfo}
                    promoText={resolvedCrossSale.promoText}
                    flagName={value.flagName}
                    name={value.name}
                    url={value.url}
                    allowance={formatVolume(offer.volume)}
                    allowancePromo={formatVolume(
                      applyDiscount(offer, offer.promo).volume
                    )}
                    onClick={() => {
                      window.dataLayer?.push({
                        ecommerce: null,
                      });
                      window.dataLayer?.push(
                        generateDataLayerClickItemList(
                          offer,
                          destinationTypeSwitcher
                        )
                      );
                    }}
                    hasPromo={value.hasPromo}
                  />
                );
            }
          })}
        {destinationTypeSwitcher === EDestinationSwitcher.ALL_DESTINATIONS &&
          Object.values(destinations).map(value => {
            if (value.offers.length > 0) {
              const offer = value.offers.find(
                o => o.duration === selectedDuration
              );

              if (offer)
                return (
                  <DestinationCard
                    key={offer.zone}
                    allowanceInfo={resolvedCrossSale.allowanceInfo}
                    promoText={resolvedCrossSale.promoText}
                    flagName={value.flagName}
                    name={value.name}
                    url={value.url}
                    allowance={formatVolume(offer.volume)}
                    allowancePromo={formatVolume(
                      applyDiscount(offer, offer.promo).volume
                    )}
                    onClick={() =>
                      window.dataLayer?.push(
                        generateDataLayerClickItemList(
                          offer,
                          destinationTypeSwitcher
                        )
                      )
                    }
                    hasPromo={value.hasPromo}
                  />
                );
            }
          })}
        {destinationTypeSwitcher === EDestinationSwitcher.ZONES &&
          Object.values(zones).map(value => {
            if (value.offers.length > 0) {
              const offer = value.offers.find(
                o => o.duration === selectedDuration
              );

              if (offer)
                return (
                  <DestinationCard
                    key={offer.zone}
                    allowanceInfo={resolvedCrossSale.allowanceInfo}
                    promoText={resolvedCrossSale.promoText}
                    flagName={value.flagName}
                    name={value.name}
                    url={value.url}
                    allowance={formatVolume(offer.volume)}
                    allowancePromo={formatVolume(
                      applyDiscount(offer, offer.promo).volume
                    )}
                    onClick={() =>
                      window.dataLayer?.push(
                        generateDataLayerClickItemList(
                          offer,
                          destinationTypeSwitcher
                        )
                      )
                    }
                    hasPromo={value.hasPromo}
                  />
                );
            }
          })}
      </div>
      {action && !isExpanded && (
        <div className={'mt-6 flex w-full justify-center lg:justify-start'}>
          <CrossSaleAction
            action={action}
            locale={locale}
            setIsExpanded={setIsExpanded}
          />
        </div>
      )}
    </div>
  );
};
